
import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

export interface RingCentralSettings {
  id: string;
  client_id: string;
  client_secret: string;
  jwt_token: string;
  phone_number: string;
  server_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useRingCentralSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [settings, setSettings] = useState<RingCentralSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ringcentral_settings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching RingCentral settings:', error);
        setError(error.message);
        return;
      }

      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient]);

  const updateSettings = async (updates: Partial<RingCentralSettings>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return false;

    if (!settings) {
      // Create new settings if none exist - ensure required fields are present
      if (!updates.client_id || !updates.client_secret || !updates.jwt_token || !updates.phone_number) {
        toast({
          title: "Error",
          description: "Client ID, Client Secret, JWT Token, and Phone Number are required",
          variant: "destructive",
        });
        return false;
      }

      try {
        const newSettings = {
          client_id: updates.client_id,
          client_secret: updates.client_secret,
          jwt_token: updates.jwt_token,
          phone_number: updates.phone_number,
          server_url: updates.server_url || 'https://platform.ringcentral.com',
          is_active: updates.is_active ?? true
        };

        const { data, error } = await supabase
          .from('ringcentral_settings')
          .insert(newSettings)
          .select()
          .single();

        if (error) {
          console.error('Error creating RingCentral settings:', error);
          toast({
            title: "Error",
            description: "Failed to create RingCentral settings",
            variant: "destructive",
          });
          return false;
        }

        setSettings(data);
        toast({
          title: "Settings Created",
          description: "RingCentral settings have been created successfully",
          className: "bg-status-success text-white"
        });
        return true;
      } catch (err) {
        console.error('Unexpected error:', err);
        return false;
      }
    }

    try {
      const { data, error } = await supabase
        .from('ringcentral_settings')
        .update(updates)
        .eq('id', settings.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating RingCentral settings:', error);
        toast({
          title: "Error",
          description: "Failed to update RingCentral settings",
          variant: "destructive",
        });
        return false;
      }

      setSettings(data);
      toast({
        title: "Settings Updated",
        description: "RingCentral settings have been updated successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      return false;
    }
  };

  useEffect(() => {
    const checkAndFetch = async () => {
      const supabase = await getSupabaseClient();
      if (supabase) {
        fetchSettings();
      }
    };
    checkAndFetch();
  }, [fetchSettings, getSupabaseClient]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetchSettings: fetchSettings
  };
};
